import { useMemo, useState, useEffect } from "react";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import HStack from "@ui-kit/layout/Stack/HStack.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { domElementIds } from "@shared/constants/common";
import InformationBanner from "@ui-kit/data-display/InformationBanner/InformationBanner.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import { useTranslation } from "@shared/utils/internationalization";
import Link from "@ui-kit/navigation/Link/Link.web";
import { formatTimer, useTimer } from "@ui-kit/feedback/timer/useTimer";
import { isValid } from "date-fns";
import { getSafeTZDate } from "@shared/utils/dates/parsing";
import { FormattedRating } from "@shared/modules/ratings/types/ratings";
import { RatingDisplay } from "@shared/modules/ratings/components/RatingDisplay.web";

const { headerDetailsId } = domElementIds;

function saleStartsOnToInstant(saleStartsOn: string, timezone: string): Date {
  return getSafeTZDate(saleStartsOn, timezone) ?? new Date(NaN);
}

export interface HeaderDetailsProps {
  title: string;
  subtitle?: string;
  organizer?: NestedPublicVenueSerializer;
  saleStartsOn?: string;
  timezone?: string;
  statusElement?: JSX.Element;
  imageElement: JSX.Element;
  badgeElement: JSX.Element;
  isSmallerThanSm: boolean;
  isTablet: boolean;
  isModal?: boolean;
  openContactDetailsModal?: () => void;
  isLoading: boolean;
  fontSize?: "small" | "regular";
  columnGap?: string;
  onClickRating?: () => void;
  rating?: FormattedRating | null;
}

const HeaderDetails = ({
  saleStartsOn = undefined,
  title = "",
  subtitle = "",
  organizer = undefined,
  statusElement = <></>,
  imageElement = <></>,
  badgeElement = <></>,
  openContactDetailsModal = () => {
    /* empty */
  },
  isSmallerThanSm = false,
  isTablet = false,
  isLoading = false,
  timezone = "",
  isModal = false,
  fontSize = "regular",
  onClickRating,
  rating,
  columnGap = "element.3xl",
}: HeaderDetailsProps): JSX.Element => {
  const skeletonText = "skeletonText";

  const { t } = useTranslation("common");

  const orgClicker = (e: React.MouseEvent) => {
    e.preventDefault();
    openContactDetailsModal();
  };

  const orgUrl = organizer ? `/o/${organizer.slug}/` : null;

  const saleStartsOnTarget = useMemo(
    () =>
      saleStartsOn && timezone
        ? saleStartsOnToInstant(saleStartsOn, timezone)
        : new Date(NaN),
    [saleStartsOn, timezone]
  );

  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  const saleStartsOnPassed =
    !saleStartsOn ||
    !timezone ||
    !isValid(saleStartsOnTarget) ||
    Date.now() >= saleStartsOnTarget.getTime();

  const [timeRemaining] = useTimer(
    !saleStartsOnPassed ? saleStartsOnTarget : undefined
  );
  const { hours, minutes, seconds } = formatTimer(timeRemaining);

  const showSaleStartsOn =
    isClient && saleStartsOn && !saleStartsOnPassed && timeRemaining > 0;

  const saleStartsOnComponent = (
    <Flex
      w="100%"
      flexDirection="column"
      data-testid="event-details-header-sale-starts-on"
    >
      <Text
        as="h3"
        fontSize="xl"
        fontWeight="bold"
        width="100%"
        color="purple.fg"
        mb="xs"
      >
        {t("Sale starts in")}
      </Text>
      <Flex direction="row" width="100%">
        <Box
          width="33%"
          padding="xs"
          textAlign="center"
          bg="purple.subtle"
          borderRadius="md"
          marginBottom="lg"
        >
          <Text fontWeight="bold" fontSize="4xl" color="purple.fg">
            {timeRemaining > 0 ? hours : "0"}
          </Text>
          <Text color="purple.fg">{t("Hour", { count: Number(hours) })}</Text>
        </Box>
        <Box
          width="33%"
          padding="xs"
          textAlign="center"
          bg="purple.subtle"
          borderRadius="md"
          marginLeft="xs"
          marginRight="xs"
          marginBottom="lg"
        >
          <Text fontWeight="bold" fontSize="4xl" color="purple.fg">
            {timeRemaining > 0 ? minutes : "0"}
          </Text>
          <Text color="purple.fg">
            {t("Minute", { count: Number(minutes) })}
          </Text>
        </Box>
        <Box
          width="33%"
          padding="xs"
          textAlign="center"
          bg="purple.subtle"
          borderRadius="md"
          marginBottom="lg"
        >
          <Text fontWeight="bold" fontSize="4xl" color="purple.fg">
            {timeRemaining > 0 ? seconds : "0"}
          </Text>
          <Text color="purple.fg">
            {t("Second", { count: Number(seconds) })}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );

  const mobileComponent: JSX.Element = (
    <Box id={headerDetailsId} data-testid="event-details-header-mobile" bg="bg">
      <Skeleton isLoaded={!isLoading}>
        <Box data-testid="event-image">{imageElement}</Box>
      </Skeleton>
      <Box minWidth={columnGap} />
      <Flex padding="16px 20px" direction="column">
        {showSaleStartsOn ? saleStartsOnComponent : null}
        <Skeleton width="100%" isLoaded={!isLoading}>
          <Text
            as="h1"
            fontSize="lg"
            fontWeight="bold"
            color="fg"
            data-testid="title"
          >
            {title || skeletonText}
          </Text>
        </Skeleton>
        <Text as="h2" fontSize="md" color="fg.muted" data-testid="subtitle">
          {subtitle}
        </Text>
        {organizer && orgUrl && (
          <Flex alignItems="center" flexWrap="wrap">
            <Text
              as="h3"
              fontSize="sm"
              color="fg.muted"
              data-testid="organizer"
            >
              <Link href={orgUrl} onClick={orgClicker}>
                {organizer.name}
              </Link>
            </Text>
            <RatingDisplay
              rating={rating}
              fontSize="xs"
              onClick={onClickRating}
            />
          </Flex>
        )}
        {statusElement}
        <Skeleton isLoaded={!isLoading}>{badgeElement}</Skeleton>

        {
          // eslint-disable-next-line no-constant-condition
          false ? (
            <Box mt="md">
              <InformationBanner.Info data-testid="hurry-banner">
                <InformationBanner.Header>Hurry!</InformationBanner.Header>
                <InformationBanner.Content>
                  This is usually purchased 3 days in advance
                </InformationBanner.Content>
              </InformationBanner.Info>
            </Box>
          ) : null
        }
      </Flex>
    </Box>
  );

  const tabletComponent: JSX.Element = (
    <Flex
      padding="24px 0px"
      data-testid="tablet-header-details"
      id={headerDetailsId}
      bg="bg"
    >
      <Skeleton borderRadius="md" isLoaded={!isLoading}>
        <Box data-testid="event-image">{imageElement}</Box>
      </Skeleton>
      <Box minWidth={columnGap} />
      <Flex direction="column" alignItems="flex-start">
        {showSaleStartsOn ? saleStartsOnComponent : null}
        <Skeleton
          width="100%"
          isLoaded={!isLoading}
          {...(isLoading && {
            mb: "8px",
          })}
        >
          <Text
            as="h1"
            fontSize={fontSize === "small" ? "lg" : "3xl"}
            fontWeight="bold"
            color="fg"
            data-testid="title"
          >
            {title || skeletonText}
          </Text>
        </Skeleton>
        <Text
          as="h2"
          fontSize={fontSize === "small" ? "sm" : "lg"}
          color="fg.muted"
          data-testid="subtitle"
        >
          {subtitle}
        </Text>
        {organizer && orgUrl && (
          <Flex alignItems="center" flexWrap="wrap">
            <Text
              as="h3"
              fontSize={fontSize === "small" ? "sm" : "md"}
              color="fg.muted"
              data-testid="organizer"
            >
              <Link href={orgUrl} onClick={orgClicker}>
                {organizer.name}
              </Link>
            </Text>
            <RatingDisplay
              rating={rating}
              fontSize={fontSize === "small" ? "xs" : "sm"}
              onClick={onClickRating}
            />
          </Flex>
        )}
        {statusElement}
        <Skeleton isLoaded={!isLoading}>{badgeElement}</Skeleton>
      </Flex>
    </Flex>
  );

  const desktopComponent: JSX.Element = (
    <Flex
      padding="24px 0px"
      id={headerDetailsId}
      data-testid="event-details-header-desktop"
      bg="bg"
    >
      <HStack width="full" alignItems="flex-start" alignContent="flex-start">
        <VStack
          minWidth="60%"
          maxWidth="60%"
          marginInlineStart={0}
          flexDirection="column"
        >
          <Skeleton
            width="100%"
            height="100%"
            borderRadius="md"
            isLoaded={!isLoading}
          >
            <Box data-testid="event-image">{imageElement}</Box>
          </Skeleton>
        </VStack>
        <Box
          minWidth={columnGap}
          marginInlineStart={0}
          flexDirection="column"
        />
        <VStack
          width="40%"
          alignItems="flex-start"
          data-testid="details"
          marginInlineStart={0}
          flexDirection="column"
        >
          {showSaleStartsOn ? saleStartsOnComponent : null}
          <Flex direction="column">
            <Skeleton width="100%" isLoaded={!isLoading}>
              <Text
                as="h1"
                fontSize={fontSize === "small" ? "lg" : "3xl"}
                fontWeight="bold"
                color="fg"
                data-testid="title"
                wordBreak="break-word"
              >
                {title || skeletonText}
              </Text>
            </Skeleton>
            {subtitle && (
              <Text
                as="h2"
                fontSize={fontSize === "small" ? "sm" : "lg"}
                color="fg.muted"
                pb="xs"
                data-testid="subtitle"
              >
                {subtitle}
              </Text>
            )}
            {organizer && orgUrl && (
              <Flex alignItems="center" flexWrap="wrap">
                <Text
                  as="h3"
                  fontSize={fontSize === "small" ? "sm" : "md"}
                  color="fg.muted"
                  data-testid="organizer"
                >
                  <Link href={orgUrl} onClick={orgClicker}>
                    {organizer.name}
                  </Link>
                </Text>
                <RatingDisplay
                  rating={rating}
                  fontSize={fontSize === "small" ? "xs" : "sm"}
                  onClick={onClickRating}
                />
              </Flex>
            )}
            {statusElement}
            <Skeleton isLoaded={!isLoading}>{badgeElement}</Skeleton>
          </Flex>
        </VStack>
      </HStack>
    </Flex>
  );
  const getComponent = () => {
    if (isSmallerThanSm) {
      return mobileComponent;
    }

    if (isTablet || isModal) {
      return tabletComponent;
    }

    return desktopComponent;
  };

  return (
    <Flex direction="column" bg="bg" width="100%">
      {getComponent()}
    </Flex>
  );
};

export default HeaderDetails;
